import React, { useState } from "react";
import { Linking, Platform, Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { useFileUpload } from "@/components/shared/UploadFile";
import {
  ACCEPTED_FILE_TYPES,
  displayAddress,
  fileKindOf,
  fileNameOf,
  hrefOf,
  titleFromFileName,
  validateLink,
} from "./links";
import { notify } from "@/components/shared/AppToast";
import Button, { ButtonRow } from "./Button";
import { TextField } from "./Field";
import IconButton from "./IconButton";
import { colors, radius, text } from "./theme";
import { t } from "@/localization";

// Links and files shown under the video on the event page.
//
// The list shows each item compactly: what guests see (its name) and where it
// goes (the address, or the file's type and name; never the storage URL).
// Adding or editing happens in a small inline form with its own Add/Save
// button, so a half-typed link never blocks saving the rest of the screen and
// errors only appear after the user tried to add it or left the field.
// Every change to the list calls onChange(links) and is saved automatically.

const open = (url) => {
  const href = hrefOf(url);
  if (Platform.OS === "web") window.open(href, "_blank", "noopener");
  else Linking.openURL(href).catch(() => {});
};

const ItemRow = ({ item, onEdit, onRemove, last }) => {
  const isFile = !!item.file_upload;
  const subtitle = isFile
    ? `${fileKindOf(item.url)} · ${fileNameOf(item.url)}`
    : displayAddress(item.url);
  return (
    <View
      style={[
        tw`flex-row items-center px-3 py-2.5`,
        !last && { borderBottomWidth: 1, borderBottomColor: colors.divider },
      ]}
    >
      <View
        style={[
          tw`items-center justify-center mr-3`,
          {
            width: 36,
            height: 36,
            borderRadius: radius.control,
            backgroundColor: colors.surfaceStrong,
          },
        ]}
      >
        <Ionicons
          name={isFile ? "document-text-outline" : "link-outline"}
          size={18}
          color={colors.slate}
        />
      </View>
      <Pressable
        style={tw`flex-1 pr-2`}
        onPress={onEdit}
        accessibilityLabel={
          item.title ? t("Edit {title}", { title: item.title }) : t("Edit link")
        }
      >
        <Text
          numberOfLines={1}
          style={
            item.title
              ? text.bodyStrong
              : [text.body, { color: colors.warning }]
          }
        >
          {item.title || t("Add a name")}
        </Text>
        <Text numberOfLines={1} style={text.hint}>
          {subtitle || t("Add a web address")}
        </Text>
      </Pressable>
      {!!item.url && (
        <IconButton
          icon="open-outline"
          label={
            item.title
              ? t("Open {title}", { title: item.title })
              : t("Open link")
          }
          onPress={() => open(item.url)}
        />
      )}
      <IconButton
        icon="create-outline"
        label={
          item.title ? t("Edit {title}", { title: item.title }) : t("Edit link")
        }
        onPress={onEdit}
      />
      <IconButton
        icon="trash-outline"
        label={
          item.title
            ? t("Remove {title}", { title: item.title })
            : t("Remove link")
        }
        onPress={onRemove}
      />
    </View>
  );
};

const Editor = ({ draft, setDraft, onSave, onCancel, isNew }) => {
  const [wide, setWide] = useState(false);
  const errors = validateLink(draft);
  const show = (field) =>
    (draft.submitted || draft.touched?.[field]) && errors[field];
  // Functional updates: a field's blur can arrive after the Add press.
  const update = (patch) => setDraft((d) => (d ? { ...d, ...patch(d) } : d));
  // Leaving an empty field isn't an error yet (only pressing Add is): an
  // error appearing on blur would move the Add button away from the click.
  const touch = (field) =>
    update((d) =>
      String(d[field] || "").trim()
        ? { touched: { ...(d.touched || {}), [field]: true } }
        : {}
    );
  const isFile = !!draft.file_upload;
  const submit = () => {
    // A file's address comes from the upload; only its name can be wrong.
    const blocking = isFile
      ? errors.title
        ? { title: errors.title }
        : {}
      : errors;
    if (Object.keys(blocking).length) {
      update(() => ({ submitted: true }));
      return;
    }
    onSave({
      title: draft.title.trim(),
      url: isFile ? draft.url : draft.url.trim(),
      file_upload: isFile,
    });
  };
  return (
    <View
      onLayout={(e) => setWide(e.nativeEvent.layout.width >= 520)}
      style={[tw`p-3`, { backgroundColor: colors.surface }]}
    >
      <View style={wide && !isFile ? tw`flex-row gap-3` : undefined}>
        <TextField
          fieldStyle={wide && !isFile ? tw`flex-1` : undefined}
          label={isFile ? t("File name guests see") : t("Link text")}
          placeholder={
            isFile ? t("e.g. Order of service") : t("e.g. Our registry")
          }
          value={draft.title}
          onChangeText={(title) => update(() => ({ title }))}
          onBlur={() => touch("title")}
          error={show("title")}
          autoFocus={isNew && Platform.OS === "web"}
          returnKeyType="next"
          last={isFile}
        />
        {!isFile && (
          <TextField
            fieldStyle={wide ? { flex: 1.4 } : undefined}
            label={t("Web address")}
            placeholder="zola.com/our-registry"
            value={draft.url}
            onChangeText={(url) => update(() => ({ url }))}
            onBlur={() => touch("url")}
            error={show("url")}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType={Platform.OS === "ios" ? "url" : "default"}
            onSubmitEditing={submit}
            returnKeyType="done"
            last
          />
        )}
      </View>
      {isFile && (
        <Text style={[text.hint, tw`mt-2`]} numberOfLines={1}>
          {fileKindOf(draft.url)} · {fileNameOf(draft.url)}
        </Text>
      )}
      <ButtonRow style={tw`mt-3`}>
        <Button
          text={t("Cancel")}
          variant="tertiary"
          size="md"
          fullWidth={false}
          onPress={onCancel}
        />
        <Button
          text={isNew ? t("Add link") : t("Save")}
          variant="primary"
          size="md"
          fullWidth={false}
          onPress={submit}
        />
      </ButtonRow>
    </View>
  );
};

const LinksEditor = ({
  links = [],
  onChange,
  uploadPath,
  bucket,
  disabled,
}) => {
  // { index: number | "new", title, url, file_upload, touched, submitted }
  const [draft, setDraft] = useState(null);
  const { pick, loading: uploading } = useFileUpload({
    type: ACCEPTED_FILE_TYPES,
    path: uploadPath,
    bucket,
    onComplete: async ({ url, name }) => {
      onChange([
        ...(links || []),
        { title: titleFromFileName(name), url, file_upload: true },
      ]);
      notify.success(
        t("File added"),
        t("Guests can open it from the event page.")
      );
    },
  });

  const items = links || [];
  const save = (index) => (item) => {
    const next = [...items];
    if (index === "new") next.push(item);
    else next[index] = item;
    onChange(next);
    setDraft(null);
  };
  const remove = (index) => {
    const removed = items[index];
    const next = items.filter((_, i) => i !== index);
    onChange(next);
    if (draft && draft.index === index) setDraft(null);
    const name = removed.title || displayAddress(removed.url);
    notify.info(
      name ? t('Removed "{title}"', { title: name }) : t('Removed "link"'),
      t("Tap to undo"),
      {
        onPress: () => onChange(items),
        visibilityTime: 6000,
      }
    );
  };

  return (
    <View>
      {(items.length > 0 || draft?.index === "new") && (
        <View
          style={[
            tw`mb-3 overflow-hidden`,
            {
              borderWidth: 1,
              borderColor: colors.border,
              borderRadius: radius.card,
            },
          ]}
        >
          {items.map((item, index) =>
            draft?.index === index ? (
              <Editor
                key={`edit-${index}`}
                draft={draft}
                setDraft={setDraft}
                onSave={save(index)}
                onCancel={() => setDraft(null)}
              />
            ) : (
              <ItemRow
                key={`${index}-${item.url}`}
                item={item}
                last={index === items.length - 1 && draft?.index !== "new"}
                onEdit={() =>
                  !disabled &&
                  setDraft({ ...item, index, touched: {}, submitted: false })
                }
                onRemove={() => !disabled && remove(index)}
              />
            )
          )}
          {draft?.index === "new" && (
            <View
              style={
                items.length
                  ? { borderTopWidth: 1, borderTopColor: colors.divider }
                  : undefined
              }
            >
              <Editor
                isNew
                draft={draft}
                setDraft={setDraft}
                onSave={save("new")}
                onCancel={() => setDraft(null)}
              />
            </View>
          )}
        </View>
      )}
      {draft?.index !== "new" && (
        <ButtonRow align="start">
          <Button
            text={t("Add link")}
            icon="add"
            size="md"
            fullWidth={false}
            disabled={disabled}
            onPress={() =>
              setDraft({
                index: "new",
                title: "",
                url: "",
                file_upload: false,
                touched: {},
                submitted: false,
              })
            }
          />
          <Button
            text={uploading ? t("Uploading…") : t("Upload file")}
            icon="cloud-upload-outline"
            size="md"
            fullWidth={false}
            loading={uploading}
            disabled={disabled}
            onPress={pick}
          />
        </ButtonRow>
      )}
    </View>
  );
};

export default LinksEditor;
